import { Component, Inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
    CancellationModel,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    DateUtilities,
    MATERIAL_SOURCE_CODE,
    MaterialModel,
    MaterialService,
    ModalClass,
    NOTIFICATION_STATUS_CODE,
    NotificationModel,
    NotificationService,
    NotificationUpdateService,
    NotificationUtilities,
    StatusModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import {
    faBan,
    faBox,
    faExclamationTriangle,
    faFileInvoice,
    faPhone,
    faUsers,
    IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import {
    CancellationComponent,
    ICancellationResult,
} from '../../../shared/components/cancellation/cancellation.component';

@Component({
    selector: 'pace-dashboard-notification',
    templateUrl: './dashboard-notification.component.html',
    styleUrls: ['./dashboard-notification.component.scss'],
})
export class DashboardNotificationComponent implements OnInit {
    @Input()
    public notification: NotificationModel;

    @Input()
    public effectCodeIcon: IconDefinition;

    @Input()
    public effectCodeIconColorClass: 'red' | 'green' | 'orange';

    public isNotificationCancelledFlag: boolean;
    public displayContactName: boolean;
    public notificationStatus: StatusModel;
    public readonly faPhone = faPhone;
    public noOfParticipants: number;
    public noOfOrderedMaterials: number;

    public strings: {
        startDateTime: string;
        multiDayEndDateTime: string;
        name: string;
        notificationType: string;
        tidnr: string;
        productName: string;
        affectedEquipment: string;
        formsAvailable: string;
        weekday: string;
        multiDayWeekday: string;
        contactName: string;
        clarifyText: string;
    };

    public faExclamationTriangle = faExclamationTriangle;
    public faBan = faBan;
    public faFileInvoice = faFileInvoice;
    public faBox = faBox;
    public faParticipants = faUsers;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly notificationUtilities: NotificationUtilities,
        public readonly notificationService: NotificationService,
        private readonly notificationUpdateService: NotificationUpdateService,
        private readonly router: Router,
        private readonly modalService: BsModalService,
        private readonly materialService: MaterialService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {
        this.displayContactName = this.clientManager.isPaceUS();
    }

    public ngOnInit(): void {
        this.notificationService.initializeNotificationStatus(this.notification);
        this.notificationStatus = this.notificationService.getStatus(this.notification);

        const mainEquipment = this.notification.getMainEquipment();
        if (!mainEquipment) {
            this.log.error(
                'DashboardNotificationComponent: ngOnInit: No Main Equipment available for notification ' +
                    this.notification.identifier.toString(),
            );
        }

        this.isNotificationCancelledFlag = this.notificationStatus.statusCode === NOTIFICATION_STATUS_CODE.CNCL;
        if (this.notification.isParticipantsAvailable() && this.notification.isApplicationSpecialist) {
            this.noOfParticipants = this.notification.participantSet.length;
        }

        this.noOfOrderedMaterials = this.getNoOfOrderedMaterials();

        this.strings = {
            startDateTime: this.getSingleOrMultiDayStartDateTimeString(this.notification),
            multiDayEndDateTime: this.getMultiDayEndDateTimeString(this.notification),
            name: this.notification.address.completeName,
            notificationType: this.notificationUtilities.getNotificationTypeDescription(this.notification),
            tidnr: mainEquipment ? mainEquipment.tidnr.value : '',
            productName: mainEquipment ? mainEquipment.equiShortDesc.value : '',
            affectedEquipment: this.notification.getAffectedEquipment()?.equiShortDesc.value ?? '',
            formsAvailable: this.getFormsString(this.notification),
            weekday: this.getWeekDay(this.notificationService.getNotificationStart(this.notification)),
            multiDayWeekday: this.getWeekDay(this.notificationService.getNotificationEnd(this.notification)),
            contactName: this.notification.getContactName(),
            clarifyText: this.notification.clarifyText ?? '',
        };
    }

    public navigateToDetails(): void {
        if (
            this.notificationStatus.isCancelled() ||
            (this.notificationService.isNotificationCancelled(this.notification) &&
                !this.notificationService.getCancellationConfirmationForInprogress(this.notification.identifier))
        ) {
            this.openCancellation(
                this.notification,
                this.notificationUpdateService.getNotificationUpdateKeyByIdForCancellation(
                    this.notification.identifier,
                ),
            );
        } else {
            this.router
                .navigate([
                    ConfigConstants.routeUrlDetails,
                    this.notification.identifier.frontId,
                    this.notification.identifier.qmNum,
                    this.notification.identifier.maNum,
                ])
                .catch((error: Error) =>
                    this.log.error(
                        'Error in DashboardNotificationComponent -> navigateToDetails -> router.navigate : ',
                        error,
                    ),
                );
        }
    }

    private openCancellation(notification: NotificationModel, notificationUpdateKey: string): void {
        const modalInstance = this.modalService.show(CancellationComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new CancellationModel(notification, notificationUpdateKey),
            },
            ariaLabelledBy: 'cancellation-modal-title',
        });
        modalInstance.content?.modalResult.subscribe((result: ICancellationResult) => {
            if (result && result.notificationUpdateKey) {
                this.notificationUpdateService.removeUpdate(result.notificationUpdateKey);
            }
            // Route to details screen so that user can send confirmation of in-progress notification
            if (this.notificationService.isNotificationInProgress(notification.identifier)) {
                this.navigateToDetails();
            }
        });
    }

    /**
     * Just needed from markup code to render correct date/time string.
     *
     * @param {NotificationModel} notification the notification model
     * @returns {boolean} true if the notification is a multi-day notification
     */
    public isMultiDayNotification(notification: NotificationModel): boolean {
        return this.notificationService.isMultiDayNotification(notification);
    }

    private getSingleOrMultiDayStartDateTimeString(notification: NotificationModel): string {
        const startDate = this.notificationService.getNotificationStart(notification);
        const endDate = this.notificationService.getNotificationEnd(notification);

        let dateString = DateUtilities.formatDate(startDate) + ' | ' + DateUtilities.formatTime(startDate);

        dateString += ' - ';
        if (!this.notificationService.isMultiDayNotification(notification)) {
            dateString += DateUtilities.formatTime(endDate);
        }
        return dateString;
    }

    private getMultiDayEndDateTimeString(notification: NotificationModel): string {
        const endDate = this.notificationService.getNotificationEnd(notification);
        let dateString = '';
        if (this.notificationService.isMultiDayNotification(notification)) {
            dateString = DateUtilities.formatDate(endDate) + ' | ' + DateUtilities.formatTime(endDate);
        }
        return dateString;
    }

    private getWeekDay(date: moment.Moment): string {
        return DateUtilities.formatWeekday(date);
    }

    private getNoOfOrderedMaterials(): number {
        // The number of ordered materials must NOT include those added from Bootstock or Others
        let filteredMaterials: MaterialModel[] = [];
        const materialList: MaterialModel[] = this.materialService.getAllMaterialsForNotification(
            this.notification.identifier,
        );

        if (this.notification.isMaterialAvailable() && !this.notification.isApplicationSpecialist) {
            filteredMaterials = materialList.filter((materialModel: MaterialModel) => {
                return (
                    materialModel.materialSource.sourceCode === MATERIAL_SOURCE_CODE.ORDERED &&
                    materialModel.showOnNotificationDetail
                );
            });
        }
        return filteredMaterials.length;
    }

    private getFormsString(notificationModel: NotificationModel): string {
        let formsString = '';
        if (notificationModel.isPaceFormsEnabled) {
            formsString = <string>this.translate.instant('DASHBOARD_ASSIGNMENTS_FORM_AVAILABLE_LABEL');
        }
        return formsString;
    }
}
